import React, { useState, useCallback } from 'react'
import { useRouter } from '../lib/SmartRouter'
import './EnhancedRegistrationForm.css' // Reuse the same styling

interface LoginFormData {
  username: string
  password: string
  rememberMe: boolean
}

interface StatusMessage {
  type: 'error' | 'success' | 'info' | 'warning'
  message: string
  code?: string
  timestamp: number
}

export const LoginPage: React.FC = () => {
  const { login, navigate } = useRouter()
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
    rememberMe: false
  })
  const [statusMessages, setStatusMessages] = useState<StatusMessage[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Mouse tracking for interactive background
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget
    const rect = container.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    
    container.style.setProperty('--mouse-x', `${x}%`)
    container.style.setProperty('--mouse-y', `${y}%`)
  }, [])

  // Add status message
  const addStatusMessage = (type: StatusMessage['type'], message: string, code?: string) => {
    const newMessage: StatusMessage = {
      type,
      message,
      code,
      timestamp: Date.now()
    }
    setStatusMessages(prev => [...prev, newMessage])

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setStatusMessages(prev => prev.filter(msg => msg.timestamp !== newMessage.timestamp))
    }, 5000)
  }

  // Handle input changes
  const handleInputChange = (field: keyof LoginFormData) => (value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isSubmitting) return

    // Basic validation
    if (!formData.username.trim()) {
      addStatusMessage('error', 'Username is required', 'AUTH_001')
      return
    }

    if (!formData.password.trim()) {
      addStatusMessage('error', 'Password is required', 'AUTH_002')
      return
    }

    setIsSubmitting(true)
    addStatusMessage('info', 'Signing you in...', 'AUTH_003')

    try {
      const success = await login({
        username: formData.username,
        password: formData.password
      })

      if (success) {
        addStatusMessage('success', 'Login successful! Redirecting...', 'AUTH_004')
      } else {
        addStatusMessage('error', 'Invalid username or password', 'AUTH_005')
      }
    } catch (error) {
      addStatusMessage('error', 'Login failed. Please try again.', 'AUTH_006')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div 
      className="enhanced-registration-container"
      onMouseMove={handleMouseMove}
    >
      <div className="registration-panel">
        {/* EGL Logo Section */}
        <div className="logo-section">
          <img 
            src="https://verifygold.s3.me-central-1.amazonaws.com/EGL/Logo/EGL_Logo.svg" 
            alt="EGL Logo"
          />
        </div>

        <div className="form-header">
          <h1>Sign In</h1>
        </div>

        <form onSubmit={handleSubmit} className="enhanced-form">
          {/* Username Field */}
          <div className="form-group">
            <div className="input-wrapper">
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username')(e.target.value)}
                placeholder="Username *"
                className="enhanced-input"
                required
                id="username"
                autoComplete="username"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="form-group">
            <div className="input-wrapper">
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password')(e.target.value)}
                placeholder="Password *"
                className="enhanced-input"
                required
                id="password"
                autoComplete="current-password"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Remember Me */}
          <div className="form-group">
            <label className="remember-me-label">
              <input
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => handleInputChange('rememberMe')(e.target.checked)}
                disabled={isSubmitting}
              />
              <span className="remember-me-text">Remember me</span>
            </label>
          </div>

          {/* Status Messages */}
          {statusMessages.length > 0 && (
            <div className="status-feedback-box">
              <div
                className="status-messages"
                role="status"
                aria-live="polite"
                aria-atomic="false"
              >
                {statusMessages.map((msg, index) => (
                  <div
                    key={`${msg.timestamp}-${index}`}
                    className={`status-message ${msg.type}`}
                  >
                    <span className="status-text">{msg.message}</span>
                    {msg.code && <span className="status-code">{msg.code}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="enhanced-submit-btn"
          >
            {isSubmitting ? (
              <>
                <div className="spinner"></div>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Registration Link */}
        <div className="form-footer">
          <p>
            Don't have an account?{' '}
            <button 
              type="button"
              onClick={() => navigate('/register')}
              className="login-link"
              disabled={isSubmitting}
            >
              Register here
            </button>
          </p>
        </div>
      </div>
    </div>
  )
} 